import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Package, Star, ArrowRight, Home } from 'lucide-react';
import { guestAPI, getImageUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const GuestDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    guestAPI.getDashboard()
      .then(res => setData(res.data.data))
      .catch(() => toast.error('Failed to load your dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  const { upcomingBookings = [], pastBookings = [], activeEquipment = [] } = data || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Welcome back, {user?.firstName}!</h1>
          <p className="page-subtitle">Here's an overview of your upcoming stays and rentals.</p>
        </div>
      </div>

      {/* Upcoming Stays */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
            <Calendar size={16} style={{ display: 'inline', marginRight: 6 }} />
            Upcoming Stays
          </h2>
          <button className="btn btn-sm btn-ghost" onClick={() => navigate('/guest/bookings')}>
            View all <ArrowRight size={14} />
          </button>
        </div>

        {upcomingBookings.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 0' }}>
            <Home size={40} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
            <p style={{ color: '#9CA3AF' }}>No upcoming stays.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {upcomingBookings.map(b => {
              const daysUntil = Math.ceil((new Date(b.checkIn) - new Date()) / (1000 * 60 * 60 * 24));
              const coverImg = b.property?.images?.find(i => i.isCover) || b.property?.images?.[0];
              return (
                <div key={b._id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ height: 160, background: '#F3F4F6', position: 'relative' }}>
                    {coverImg?.url ? (
                      <img src={getImageUrl(coverImg.url)} alt={b.property?.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Home size={40} color="#D1D5DB" />
                      </div>
                    )}
                    <div style={{
                      position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.6)',
                      color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600
                    }}>
                      {daysUntil <= 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow!' : `In ${daysUntil} days`}
                    </div>
                  </div>
                  <div style={{ padding: 16 }}>
                    <h3 style={{ fontWeight: 600, marginBottom: 6 }}>{b.property?.title}</h3>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                      {format(new Date(b.checkIn), 'MMM d')} – {format(new Date(b.checkOut), 'MMM d, yyyy')} · {b.nights} nights
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: 'center' }}
                        onClick={() => navigate(`/guest/check-in/${b._id}`)}>
                        Check-in Info
                      </button>
                      <span className={`badge ${b.status === 'confirmed' ? 'badge-green' : 'badge-yellow'}`} style={{ display: 'flex', alignItems: 'center' }}>
                        {b.status}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Awaiting Reviews */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
              <Star size={16} style={{ display: 'inline', marginRight: 6 }} />
              Leave a Review
            </h2>
          </div>
          {pastBookings.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
              No completed stays to review
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {pastBookings.map(b => (
                <div key={b._id} className="card card-sm" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{b.property?.title}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                      {b.checkOut ? format(new Date(b.checkOut), 'MMM d, yyyy') : ''}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-outline">
                    <Star size={14} /> Rate Stay
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Equipment */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
              <Package size={16} style={{ display: 'inline', marginRight: 6 }} />
              Active Equipment Rentals
            </h2>
          </div>
          {activeEquipment.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
              No active equipment rentals
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {activeEquipment.map(b => (
                <div key={b._id} className="card card-sm" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{b.equipment?.name}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                      Due: {b.endDate ? format(new Date(b.endDate), 'MMM d, yyyy') : ''}
                    </div>
                  </div>
                  <span className={`badge ${b.status === 'picked_up' ? 'badge-blue' : 'badge-green'}`}>
                    {b.status?.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestDashboard;
